import React, { useState, useCallback } from "react";
import "moment/locale/pt-br";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { ModuleHeader } from '~/components/ModuleHeader';
import { Icon, Radio, Dropdown, Menu, Popover, Badge, Button } from "antd";
import InstagramImport from "./InstagramImport";
import InstagramConnect from "./InstagramConnect";
import DateNavigation from '~/_shared/components/DateNavigation';
import Search from '~/_shared/components/Search';
import FilterPillsBar from '~/_shared/components/FilterPillsBar';
import {
  formatSocialSellingDate,
  isSocialSellingPeriodDefault,
  countActiveSocialSellingFilters,
} from '../filters';

const CadenceBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #f0f5ff;
  border: 1px solid #adc6ff;
  border-radius: 20px;
  cursor: pointer;
  color: #1890ff;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  margin-left: 12px;
  max-width: 280px;

  &:hover {
    background: #e6f7ff;
    border-color: #1890ff;
  }

  .anticon-close {
    font-size: 10px;
    color: #1890ff;
    opacity: 0.7;
    transition: opacity 0.2s;
  }

  &:hover .anticon-close {
    opacity: 1;
  }
`;

var DISPLAY_MODE_MAP = {
  funnel: { label: 'Funil', color: '#722ed1', icon: 'funnel-plot' },
  table: { label: 'Tabela', color: '#1890ff', icon: 'table' },
};

const truncateString = function(str, maxLength) {
  if (maxLength === undefined) maxLength = 25;
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength) + '...';
};

export const Header = ({
  showDrawer,
  displayMode,
  updateDisplayMode,
  searchQuery,
  setSearchQuery,
  date,
  setDate,
  dateMode,
  setDateMode,
  selectedCadence,
  setSelectedCadence,
  defaultFilters,
  onClearFilters,
}) => {
  var [isInstagramImportVisible, setIsInstagramImportVisible] = useState(false);
  var allCadences = useSelector(function(state) { return state.settings['socialSelling.cadence']; });

  // Redux selectedMonth follows the applied date in the page (index.jsx).
  var handleDateChange = useCallback(function(newDate) {
    setDate(newDate);
  }, [setDate]);

  var parsedCadences = React.useMemo(function() {
    if (!allCadences) return [];
    return allCadences.map(function(cadence) {
      try {
        var parsedValue = JSON.parse(cadence.value);
        return {
          id: cadence.id,
          name: parsedValue.name,
          data: parsedValue.data
        };
      } catch (e) {
        console.error('Error parsing cadence:', e);
        return null;
      }
    }).filter(Boolean);
  }, [allCadences]);

  var selectedCadenceName = React.useMemo(function() {
    var cadence = parsedCadences.find(function(c) { return c.id === selectedCadence; });
    return cadence ? truncateString(cadence.name) : null;
  }, [parsedCadences, selectedCadence]);

  var handleClearCadence = useCallback(function() {
    setSelectedCadence(null);
  }, [setSelectedCadence]);

  // When no cadence is selected, show minimal header
  var showCadenceControls = selectedCadence !== null;

  var handleMenuClick = useCallback(function(e) {
    if (e.key === 'single') {
      showDrawer({});
    } else if (e.key === 'instagram') {
      setIsInstagramImportVisible(true);
    }
  }, [showDrawer]);

  var handleCloseInstagramImport = useCallback(function() {
    setIsInstagramImportVisible(false);
  }, []);

  var newLeadMenu = (
    <Menu onClick={handleMenuClick}>
      <Menu.Item key="single">
        <Icon type="user" style={{ marginRight: 8 }} />
        Lead único
      </Menu.Item>
      <Menu.Item key="instagram">
        <Icon type="instagram" style={{ marginRight: 8, color: '#E1306C' }} />
        Importar do Instagram
      </Menu.Item>
    </Menu>
  );

  // Applied filters (period + search); the display mode is a view, not a filter.
  var defaults = defaultFilters || { date: date, dateMode: dateMode, search: '' };
  var appliedFilters = { date: date, dateMode: dateMode, search: searchQuery || '' };
  var isPeriodDefault = isSocialSellingPeriodDefault(appliedFilters, defaults);

  // Count active filters: only what differs from the defaults
  var filterCount = showCadenceControls ? countActiveSocialSellingFilters(appliedFilters, defaults) : 0;

  // Build pills
  var pills = [];
  if (showCadenceControls) {
    var modeInfo = DISPLAY_MODE_MAP[displayMode] || DISPLAY_MODE_MAP.funnel;
    pills.push({ key: 'mode', label: modeInfo.label, color: modeInfo.color, icon: modeInfo.icon });
    if (date) {
      pills.push({
        key: 'date',
        label: formatSocialSellingDate(date, dateMode),
        color: '#1890ff',
        icon: 'calendar',
        onRemove: isPeriodDefault ? undefined : function() {
          setDateMode(defaults.dateMode);
          setDate(defaults.date);
        },
      });
    }
    if (searchQuery) {
      pills.push({
        key: 'search',
        label: 'Busca: ' + searchQuery,
        color: '#722ed1',
        icon: 'search',
        onRemove: function() { setSearchQuery(''); },
      });
    }
  }

  var handleClearAllFilters = function() {
    if (onClearFilters) onClearFilters();
  };

  return (
    <>
      <InstagramImport
        visible={isInstagramImportVisible}
        onCancel={handleCloseInstagramImport}
      />
      <ModuleHeader
        breadcrumbs={showCadenceControls && selectedCadenceName
          ? ['CRM', { label: 'Social Selling', onClick: handleClearCadence }, selectedCadenceName]
          : ['CRM', 'Social Selling']
        }
        search={showCadenceControls ? (
          <Search
            value={searchQuery}
            onChange={function(e) { setSearchQuery(e.target.value); }}
            placeholder="Buscar leads"
          />
        ) : null}
        title={<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span>{showCadenceControls && selectedCadenceName ? selectedCadenceName : 'Social Selling'}</span>
        </div>}
        center={showCadenceControls ? (
          <>
          <Popover
            trigger="click"
            placement="bottomRight"
            content={
              <div style={{ width: 360 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>Filtros</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {filterCount > 0 && (
                      <a
                        style={{ fontSize: 12, color: '#f5222d' }}
                        onClick={handleClearAllFilters}
                      >
                        Limpar tudo
                      </a>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {/* Display mode */}
                  <div>
                    <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Visualização</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {[
                        { value: 'funnel', label: 'Funil', color: '#722ed1', icon: 'funnel-plot' },
                        { value: 'table', label: 'Tabela', color: '#1890ff', icon: 'table' },
                      ].map(function(opt) {
                        var isActive = displayMode === opt.value;
                        return (
                          <div
                            key={opt.value}
                            onClick={function() { updateDisplayMode(opt.value); }}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
                              border: '1.5px solid ' + (isActive ? opt.color : '#e8e8e8'),
                              background: isActive ? opt.color + '0a' : '#fafafa',
                              transition: 'all 0.2s',
                            }}
                          >
                            <span style={{ fontSize: 12, color: isActive ? '#262626' : '#8c8c8c' }}>
                              <Icon type={opt.icon} style={{ marginRight: 4 }} />
                              {opt.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {/* Date navigation */}
                  <div>
                    <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Período</div>
                    <DateNavigation
                      date={date}
                      onChange={handleDateChange}
                      mode={dateMode}
                      onModeChange={setDateMode}
                    />
                  </div>
                </div>
              </div>
            }
          >
            <Badge
              count={filterCount}
              offset={[-4, 0]}
              style={{ backgroundColor: '#722ed1' }}
            >
              <Button
                icon="bars"
                style={{ borderRadius: 8 }}
              >
                Filtros
              </Button>
            </Badge>
          </Popover>
          <InstagramConnect />
          <Dropdown overlay={newLeadMenu} trigger={['click']}>
            <Button icon="plus" shape="circle" />
          </Dropdown>
          </>
        ) : null}
        advanced={
          pills.length > 0 ? (
            React.createElement(FilterPillsBar, {
              pills: pills,
              onClearAll: filterCount > 0 ? handleClearAllFilters : undefined,
            })
          ) : !showCadenceControls ? (
            React.createElement(FilterPillsBar, {
              pills: [{ key: 'no-cadence', label: 'Selecione uma cadência', color: '#8c8c8c', icon: 'info-circle' }],
            })
          ) : null
        }
      />
    </>
  );
};